/**
 * Provider identification code types for DORA B_05.01
 * Based on EBA technical standards for ICT third-party service provider registration
 */

export const PROVIDER_ID_TYPE_BASE_OPTIONS = [
  "LEI",   // Legal Entity Identifier (ISO 17442) - 20 chars
  "EUID",  // European Unique Identifier (EU 2021/1042)
] as const;

export const NATIONAL_ID_TYPES = [
  "CRN",  // Corporate Registration Number
  "VAT",  // VAT Number
  "PNR",  // Passport Number (individuals only)
  "NIN",  // National Identity Number (individuals only)
] as const;

/**
 * Person type options for B_05.01.0070
 * Determines which identification code types are allowed
 */
export const PERSON_TYPE_OPTIONS = [
  "Legal person, excluding individuals acting in business capacity",
  "Individual acting in a business capacity",
] as const;

export type PersonType = typeof PERSON_TYPE_OPTIONS[number];
export type ProviderIdTypeBase = typeof PROVIDER_ID_TYPE_BASE_OPTIONS[number];
export type NationalIdType = typeof NATIONAL_ID_TYPES[number];

/**
 * Generate provider ID type options based on person type
 * Legal persons: Only LEI or EUID allowed
 * Individuals: LEI, EUID, or national codes with country prefix
 */
export const getProviderIdTypeOptions = (personType?: string): string[] => {
  const isLegalPerson = personType?.includes("Legal person");

  if (isLegalPerson) {
    // Legal persons must use LEI or EUID only
    return [...PROVIDER_ID_TYPE_BASE_OPTIONS];
  }

  // Individuals can use base types or national codes with country prefix
  // Examples: "US_CRN", "DE_VAT", "FR_NIN", "GB_PNR"
  return [
    ...PROVIDER_ID_TYPE_BASE_OPTIONS,
    ...NATIONAL_ID_TYPES.map(type => `{Country}_${type}`),
  ];
};

/**
 * Validate provider ID type format
 * - LEI/EUID: Direct usage
 * - National codes: {ISO_3166_1_ALPHA_2}_{TYPE} (e.g., "US_CRN", "DE_VAT")
 */
export const isValidProviderIdType = (idType: string): boolean => {
  // Check base types
  if (PROVIDER_ID_TYPE_BASE_OPTIONS.includes(idType as any)) {
    return true;
  }

  // Check national code pattern: {Country}_{Type}
  if (idType.includes("_")) {
    const [countryCode, type] = idType.split("_");

    // Validate country code length (ISO 3166-1 alpha-2 is 2 chars)
    if (countryCode.length !== 2) {
      return false;
    }

    // Validate ID type
    return NATIONAL_ID_TYPES.includes(type as any);
  }

  return false;
};

/**
 * Validate LEI format (20-character alphanumeric, uppercase)
 */
export const isValidLEI = (lei: string): boolean => {
  return /^[A-Z0-9]{20}$/.test(lei);
};
